import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid2,
  IconButton,
  MenuItem,
  Modal,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import LeaderboardOutlinedIcon from "@mui/icons-material/LeaderboardOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { Link } from "react-router-dom";
import { useSnackbar } from "notistack";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import { ROUTES } from "constants/routes";
import performRequest from "network/perform-request";
import { classificationsIndexRoute, exportOrganizationRankingsStandignsRoute } from "network/api-routes";
import {
  groupClassificationsByModality,
  modalityLabel,
  rankingCategory,
  rankingSportId,
  rankingSportName,
} from "./classificationListUtils";
import { withStyles } from "@mui/styles";

const classificationAdminUrl = (ranking, view) =>
  `${ROUTES.ADMIN.ORGANIZATIONS}/classifications/${ranking.uuid_}?view=${view}`;

const countFrom = (ranking, countFields, collectionFields) => {
  const sources = [ranking, ranking.current_season];

  for (const source of sources) {
    if (!source) continue;

    for (const field of countFields) {
      const rawCount = source[field];
      if (rawCount === null || rawCount === undefined || rawCount === "") continue;

      const count = Number(rawCount);
      if (Number.isFinite(count) && count >= 0) return count;
    }

    for (const field of collectionFields) {
      if (Array.isArray(source[field])) return source[field].length;
    }
  }

  return null;
};

const rankingPlayersCount = (ranking) =>
  countFrom(
    ranking,
    ["ranked_players_count", "ranking_players_count", "players_count", "participants_count", "standings_count"],
    ["ranked_players", "players", "standings"],
  );

const rankingTournamentsCount = (ranking) =>
  countFrom(ranking, ["affected_tournaments_count", "ranking_tournaments_count", "tournaments_count"], ["tournaments"]);

const RankingMetric = ({ label, value, className }) => (
  <Grid2 size={6} className={className}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h5" fontWeight={700}>
      {value ?? "—"}
    </Typography>
  </Grid2>
);

const RankingCard = ({ ranking, metricStyle }) => {
  const category = rankingCategory(ranking);
  const playersCount = rankingPlayersCount(ranking);
  const tournamentsCount = rankingTournamentsCount(ranking);

  return (
    <Card
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        borderRadius: 2,
        transition: "border-color 160ms ease, box-shadow 160ms ease",
        "&:hover": {
          borderColor: "primary.main",
          boxShadow: 2,
        },
      }}
    >
      <CardContent sx={{ p: 2.5, pb: 2, flex: 1 }}>
        <Stack spacing={2} height="100%">
          <Box minWidth={0}>
            <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
              <Typography variant="h6" fontWeight={700} lineHeight={1.25}>
                {ranking.name}
              </Typography>
              {ranking.current_season?.year && (
                <Chip size="small" variant="outlined" label={ranking.current_season.year} sx={{ flexShrink: 0 }} />
              )}
            </Stack>
            {category.name && category.name !== ranking.name && (
              <Typography variant="body2" color="text.secondary" mt={0.75} noWrap>
                {category.name}
              </Typography>
            )}
            {ranking.description && (
              <Typography variant="body2" color="text.secondary" mt={0.75} noWrap>
                {ranking.description}
              </Typography>
            )}
          </Box>
          <Grid2 container spacing={1}>
            <RankingMetric className={metricStyle} value={playersCount} label="Jugadores" />
            <RankingMetric className={metricStyle} value={tournamentsCount} label="Torneos" />
          </Grid2>
        </Stack>
      </CardContent>
      <Stack direction="row" spacing={1} sx={{ px: 2.5, pb: 2.5 }}>
        <Button
          component={Link}
          to={`/rankings/${ranking.uuid_}`}
          target="_blank"
          rel="noreferrer"
          size="small"
          variant="contained"
          startIcon={<LeaderboardOutlinedIcon />}
          sx={{ flex: 1 }}
        >
          Ver tabla
        </Button>
        <IconButton
          component={Link}
          to={classificationAdminUrl(ranking, "tournaments")}
          size="small"
          variant="outlined"
        >
          <SettingsOutlinedIcon />
        </IconButton>
      </Stack>
    </Card>
  );
};

const ClassificationRowsIndex = ({ archived, classes }) => {
  const { user, selectedOrganization } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const [rankings, setRankings] = useState([]);
  const [ready, setReady] = useState(false);
  const [search, setSearch] = useState("");
  const [sportFilter, setSportFilter] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setReady(false);
    const params = {
      archived,
      ...(selectedOrganization?.id ? { organization_id: selectedOrganization.id } : {}),
    };

    performRequest("POST", classificationsIndexRoute, params, true, user)
      .then((response) => setRankings(Array.isArray(response.data) ? response.data : []))
      .catch((error) => enqueueSnackbar(error?.response?.data?.error || error.message, { variant: "error" }))
      .finally(() => setReady(true));
  }, [archived, enqueueSnackbar, selectedOrganization?.id, user]);

  const sports = useMemo(() => {
    const uniqueSports = new Map();

    rankings.forEach((ranking) => {
      uniqueSports.set(rankingSportId(ranking), {
        id: rankingSportId(ranking),
        name: rankingSportName(ranking),
      });
    });

    return Array.from(uniqueSports.values()).sort((left, right) => left.name.localeCompare(right.name, "es"));
  }, [rankings]);
  const activeSportFilter = sports.some((sport) => sport.id === sportFilter) ? sportFilter : sports[0]?.id || "";

  useEffect(() => {
    setSportFilter(activeSportFilter);
  }, [activeSportFilter]);

  const filteredRankings = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase("es");

    return rankings.filter((ranking) => {
      const category = rankingCategory(ranking);
      const searchableText = [ranking.name, ranking.description, category.name, category.ladder?.name]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase("es");

      return (
        (!normalizedSearch || searchableText.includes(normalizedSearch)) &&
        (!activeSportFilter || rankingSportId(ranking) === activeSportFilter)
      );
    });
  }, [activeSportFilter, rankings, search]);
  const groups = useMemo(() => groupClassificationsByModality(filteredRankings), [filteredRankings]);

  const handleExport = useCallback(
    (email) => {
      if (!selectedOrganization?.id) {
        enqueueSnackbar("No se ha seleccionado una organización.", { variant: "error" });
        return;
      }

      setExporting(true);
      performRequest(
        "POST",
        exportOrganizationRankingsStandignsRoute,
        { organization_id: selectedOrganization.id, mail: email },
        true,
        user,
      )
        .then(() => {
          enqueueSnackbar("El reporte será enviado a su correo electrónico en breve.", { variant: "success" });
          setExportOpen(false);
        })
        .catch((error) => enqueueSnackbar(error?.response?.data?.message || error.message, { variant: "error" }))
        .finally(() => setExporting(false));
    },
    [enqueueSnackbar, selectedOrganization?.id, user],
  );

  if (!ready) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={2}
      >
        <Box>
          <RevaText variant="h4">
            {archived ? "Rankings archivados" : "Rankings"} {selectedOrganization?.name || ""}
          </RevaText>
        </Box>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          {!archived && (
            <Button
              component={Link}
              to={`${ROUTES.ADMIN.ORGANIZATIONS}/classifications/create`}
              variant="contained"
              startIcon={<AddIcon />}
            >
              Crear ranking
            </Button>
          )}
          <Button variant="outlined" startIcon={<FileDownloadOutlinedIcon />} onClick={() => setExportOpen(true)}>
            Exportar
          </Button>
        </Stack>
      </Stack>

      <Divider />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          select
          size="small"
          label="Deporte"
          value={activeSportFilter}
          onChange={(event) => setSportFilter(event.target.value)}
          disabled={!sports.length}
          sx={{ minWidth: { sm: 220 } }}
        >
          {sports.map((sport) => (
            <MenuItem key={sport.id} value={sport.id}>
              {sport.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Buscar ranking"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ flex: 1 }}
        />
      </Stack>

      {groups.length ? (
        <Stack spacing={5}>
          {groups.map((modalityGroup) => (
            <Stack key={modalityGroup.key} spacing={3}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                spacing={1}
                pb={1.5}
                borderBottom={1}
                borderColor="divider"
              >
                <Typography variant="h5" fontWeight={700}>
                  {modalityLabel(modalityGroup.modality)}
                </Typography>
                <Chip size="small" variant="outlined" label={`${modalityGroup.rankingsCount} rankings`} />
              </Stack>

              {modalityGroup.ladders.map((ladder) => (
                <Stack key={ladder.key} spacing={1.5}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <EmojiEventsOutlinedIcon color="primary" fontSize="small" />
                    <Typography variant="h6" fontWeight={600}>
                      {ladder.ladderName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {ladder.rankings.length === 1 ? "1 ranking" : `${ladder.rankings.length} rankings`}
                    </Typography>
                  </Stack>
                  <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(min(100%, 285px), 1fr))" gap={2}>
                    {ladder.rankings.map((ranking) => (
                      <RankingCard key={ranking.id} ranking={ranking} metricStyle={classes.rankingMetric} />
                    ))}
                  </Box>
                </Stack>
              ))}
            </Stack>
          ))}
        </Stack>
      ) : (
        <Paper variant="outlined" sx={{ p: 5, textAlign: "center" }}>
          <Typography variant="h6">
            {rankings.length ? "No hay rankings que coincidan con los filtros" : "Aún no hay rankings"}
          </Typography>
          {!rankings.length && !archived && (
            <Typography variant="body2" color="text.secondary" mt={1}>
              Creá el primero con el botón de arriba.
            </Typography>
          )}
        </Paper>
      )}

      <ExportRankingsModal
        open={exportOpen}
        loading={exporting}
        onClose={() => !exporting && setExportOpen(false)}
        onSubmit={handleExport}
      />
    </Stack>
  );
};

const ExportRankingsModal = ({ open, loading, onClose, onSubmit }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  const submit = () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Ingresá un correo electrónico válido.");
      return;
    }
    setError("");
    onSubmit(email);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: 420 },
          p: 3,
        }}
      >
        <Stack spacing={2}>
          <Typography variant="h6">Exportar rankings</Typography>
          <Typography variant="body2" color="text.secondary">
            Recibirás por correo las tablas de posiciones de la organización.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="email"
            label="Correo electrónico"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setError("");
            }}
            error={Boolean(error)}
            helperText={error}
          />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={onClose} disabled={loading}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={submit} disabled={loading}>
              {loading ? "Enviando…" : "Enviar reporte"}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Modal>
  );
};

const styles = (theme) => ({
  rankingMetric: {
    backgroundColor: theme.palette.background.base,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
  },
});

export default withStyles(styles)(ClassificationRowsIndex);
